import { AddOrEditMatterModal } from "@/components/features/matter/layout/AddOrEditMatterModal/AddOrEditMatterModal";
import { MattersGeneralSearch } from "@/components/features/matters/MattersGeneralSearch";
import MattersSearchBar from "@/components/features/matters/MattersSearchBar/MattersSearchBar";
import { MattersTable } from "@/components/features/matters/MattersTable/MattersTable";
import { AppModal, AppPageShell, Button } from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useSearchObject } from "@/hooks/useSearchObject";
import { searchMattersCountQueryOptions } from "@/lib/data/react-query/matters/search-matters";
import {
  getSearchMattersCount,
  type SearchMatterStatusCategory,
} from "@/lib/data/requests/matters/search-matters";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { ACTIVE_MATTER_STATUS_CATEGORY_OPTIONS } from "@/lib/data/static/matterStatics";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { cn } from "@/lib/utils/cn";
import { ensureClientLabelFilter } from "@/lib/utils/matters/matter-filter-utils";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { HelpCircle, SlidersHorizontal, X, Plus } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";

import { useTour, type StepType } from "@reactour/tour";

type MattersListViewProps = {
  defaultPageSize?: number;
  embedded?: boolean;
  topContent?: ReactNode;
  forcedSearchObject?: { [key: string]: string[] };
  searchItemType?: "matter" | "closedMatters";
  showAssigneeFilter?: boolean;
  showCreateMatter?: boolean;
  showDroppedDateColumn?: boolean;
  showTour?: boolean;
  showStatusStartedFilter?: boolean;
  statusCategories?: SearchMatterStatusCategory[];
  syncWithSearchParams?: boolean;
  tableName?: (typeof tanstackTableNames)[keyof typeof tanstackTableNames];
};

export function MattersListView({
  defaultPageSize = 10,
  embedded = false,
  topContent,
  forcedSearchObject,
  searchItemType = "matter",
  showAssigneeFilter = true,
  showCreateMatter = true,
  showDroppedDateColumn = false,
  showTour = true,
  showStatusStartedFilter = false,
  statusCategories = [...ACTIVE_MATTER_STATUS_CATEGORY_OPTIONS],
  syncWithSearchParams = true,
  tableName = tanstackTableNames.matters,
}: MattersListViewProps) {
  const { hasActionPermission } = useUser();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);
  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(false);
  const [matterSearch, setMatterSearch] = useState("");
  const [matterCount, setMatterCount] = useState<number>();
  const [draftSearchObject, setDraftSearchObject] = useState<{
    [key: string]: string[];
  }>({});
  const debouncedMatterSearch = useDebouncedValue(matterSearch, 250);
  const {
    searchObject,
    addSearchObjectValue,
    removeSearchObjectValue,
    clearSearchObject,
    replaceSearchObject,
  } = useSearchObject({
    syncWithSearchParams,
    itemType: searchItemType,
    persistSearchObject: true,
  });

  const hasCreatePermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.create,
  );
  const showReferredBy = tableName === tanstackTableNames.matters;

  // Single tour for this page
  const steps: StepType[] = [
    {
      selector: "#matters-list-view",
      content:
        "This is the matters overview page. Here you can see all of your matters in one place.",
    },
    {
      selector: "#matters-search-bar",
      content:
        "Search matters by title, summary, or incident location. Use all filters to refine the table further.",
    },
    {
      selector: "#data-table-visibility-dropdown-trigger",
      content:
        "You can hide or show columns using this dropdown. Changing the order or visibility of columns is unique to you, and will not affect any other firm users.",
    },
    {
      selector: "#matters-table",
      content:
        "This table lists your matters. You can drag and drop columns to reorder them, click the column title to hide or sort by that column, or click on a row to go to that matter page.",
    },
  ];
  if (hasCreatePermission && showCreateMatter) {
    steps.push({
      selector: "[data-testid='create-matter-button']",
      content: "Click this button to start creating a new matter.",
      stepInteraction: true,
    });
  }

  const {
    isOpen: isTourOpen,
    currentStep,
    setIsOpen: setIsTourOpen,
    setSteps,
    setCurrentStep,
  } = useTour();
  const createButtonStepIndex = steps.findIndex(
    (s) => s.selector === "[data-testid='create-matter-button']",
  );
  const startTour = () => {
    setSteps?.(steps);
    setCurrentStep(0);
    setIsTourOpen(true);
  };
  const activeFilterCount = Object.values(searchObject).reduce(
    (count, values) => count + values.length,
    0,
  );
  const draftSearchInput = useMemo(() => {
    const mergedSearchObject = {
      ...ensureClientLabelFilter(draftSearchObject),
      ...forcedSearchObject,
    };
    const { unassigned, ...filters } = mergedSearchObject;

    return {
      ...filters,
      unassigned: unassigned?.includes("true"),
      statusCategories,
      search: debouncedMatterSearch || undefined,
    };
  }, [
    draftSearchObject,
    forcedSearchObject,
    statusCategories,
    debouncedMatterSearch,
  ]);
  const { data: draftMattersData, isFetching: isDraftMattersFetching } =
    useQuery({
      ...searchMattersCountQueryOptions(draftSearchInput),
      enabled: isMoreFiltersOpen,
    });
  const draftMatterCount = getSearchMattersCount(draftMattersData);

  const addDraftSearchObjectValue = (
    key: string,
    value: string,
    replace = false,
  ) => {
    setDraftSearchObject((current) => {
      if (replace) return { ...current, [key]: [value] };
      const values = current[key] ?? [];
      return values.includes(value)
        ? current
        : { ...current, [key]: [...values, value] };
    });
  };
  const removeDraftSearchObjectValue = (key: string, value: string) => {
    setDraftSearchObject((current) => {
      const values = (current[key] ?? []).filter((item) => item !== value);
      if (values.length > 0) {
        return { ...current, [key]: values };
      }
      return Object.fromEntries(
        Object.entries(current).filter(([currentKey]) => currentKey !== key),
      );
    });
  };
  const openMoreFilters = () => {
    setDraftSearchObject(searchObject);
    setIsMoreFiltersOpen(true);
  };

  return (
    <AppPageShell
      className="h-full min-h-0 overflow-hidden"
      padding={embedded ? "none" : "default"}
      showMobileSidebarToggle={!embedded}
    >
      {topContent}
      <div
        id="matters-list-view"
        className="flex min-h-0 min-w-0 flex-1 flex-col"
      >
        <div id="matters-table" className="flex min-h-0 flex-1 flex-col">
          <MattersTable
            chrome="matterDashboard"
            defaultPageSize={defaultPageSize}
            scrollableBody
            showRelatedMatters={tableName === tanstackTableNames.matters}
            tableName={tableName}
            showReferredByColumn={showReferredBy}
            showDroppedDateColumn={showDroppedDateColumn}
            statusCategories={statusCategories}
            onRowCountChange={setMatterCount}
            onRowClick={async (row) => {
              if (isTourOpen) {
                setIsTourOpen(false);
              }
              await navigate({ to: `/matters/${row.original.id}` });
            }}
            searchBar={
              <div
                id="matters-search-bar"
                className="flex min-w-0 flex-wrap items-center gap-2"
              >
                <MattersGeneralSearch
                  value={matterSearch}
                  onChange={setMatterSearch}
                  itemCount={matterCount}
                />
                <MattersSearchBar
                  searchObject={searchObject}
                  addSearchObjectValue={addSearchObjectValue}
                  removeSearchObjectValue={removeSearchObjectValue}
                  showAssigneeFilter={showAssigneeFilter}
                  showReferredByFilter={showReferredBy}
                  statusCategories={statusCategories}
                  showStatusStartedFilter={showStatusStartedFilter}
                  layout="quick"
                />
                <div
                  className={cn(
                    "relative inline-flex",
                    activeFilterCount > 0 &&
                      "h-8 overflow-hidden rounded-full border",
                  )}
                >
                  <Button
                    type="button"
                    variant={activeFilterCount > 0 ? "ghost" : "outline"}
                    className={cn(
                      "h-8 px-3 text-xs font-medium",
                      activeFilterCount > 0 ? "rounded-none" : "rounded-full",
                    )}
                    aria-label="All filters"
                    title="All filters"
                    onClick={() => {
                      openMoreFilters();
                    }}
                  >
                    <SlidersHorizontal />
                    All filters
                    {activeFilterCount > 0 ? (
                      <span className="bg-destructive text-destructive-foreground min-w-5 rounded-full px-1 py-0.5 text-center text-xs leading-none">
                        {activeFilterCount}
                      </span>
                    ) : null}
                  </Button>
                  {activeFilterCount > 0 ? (
                    <Button
                      type="button"
                      variant="ghost"
                      aria-label="Clear filters"
                      title="Clear filters"
                      className="h-8 w-8 rounded-none border-l p-0"
                      onClick={() => {
                        void clearSearchObject();
                      }}
                    >
                      <X />
                    </Button>
                  ) : null}
                </div>
              </div>
            }
            searchObject={searchObject}
            generalSearch={debouncedMatterSearch}
            forcedSearchObject={forcedSearchObject}
            tableToolbarBeforeColumns={
              showTour ? (
                <Button
                  aria-label="Help"
                  variant="circleIcon"
                  size="icon"
                  onClick={startTour}
                  title="Show page tour"
                >
                  <HelpCircle className="text-foreground h-5 w-5" />
                </Button>
              ) : null
            }
            tableButtons={
              showCreateMatter && hasCreatePermission ? (
                <Button
                  variant="foreground"
                  size="md"
                  onClick={() => {
                    if (
                      isTourOpen &&
                      typeof currentStep === "number" &&
                      currentStep === createButtonStepIndex
                    ) {
                      setCurrentStep((s) => s + 1);
                    }
                    setIsCreating(true);
                  }}
                  data-testid="create-matter-button"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Create matter
                </Button>
              ) : null
            }
          />
        </div>
      </div>

      <AppModal
        open={isMoreFiltersOpen}
        onOpenChange={setIsMoreFiltersOpen}
        title="All filters"
        description="Choose filters, then apply them to the matters table."
        size="lg"
        bodyContentClassName="p-0"
        footer={
          <div className="flex w-full items-center justify-between gap-3">
            <Button
              type="button"
              variant="link"
              onClick={() => {
                setDraftSearchObject({});
              }}
            >
              Clear all
            </Button>
            <Button
              type="button"
              variant="foreground"
              disabled={draftMatterCount === undefined}
              onClick={() => {
                void (Object.keys(draftSearchObject).length
                  ? replaceSearchObject(draftSearchObject)
                  : clearSearchObject());
                setIsMoreFiltersOpen(false);
              }}
            >
              {isDraftMattersFetching || draftMatterCount === undefined
                ? "Loading results..."
                : `Show ${draftMatterCount} results`}
            </Button>
          </div>
        }
      >
        <div className="p-4">
          <MattersSearchBar
            searchObject={draftSearchObject}
            addSearchObjectValue={addDraftSearchObjectValue}
            removeSearchObjectValue={removeDraftSearchObjectValue}
            showAssigneeFilter={showAssigneeFilter}
            showReferredByFilter={showReferredBy}
            statusCategories={statusCategories}
            showStatusStartedFilter={showStatusStartedFilter}
            layout="list"
          />
        </div>
      </AppModal>

      {showCreateMatter ? (
        <AddOrEditMatterModal
          open={isCreating}
          setOpen={setIsCreating}
          editing={false}
          creatingMore={false}
          allowedMatterStatusCategories={[
            ...ACTIVE_MATTER_STATUS_CATEGORY_OPTIONS,
          ]}
          afterCreateFn={(res) => navigate({ to: `/matters/${res.data.id}` })}
        />
      ) : null}
    </AppPageShell>
  );
}
